import React, { useRef, useState } from 'react';
import { Modal, message } from 'antd';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

/**
 * Manual crop dialog for a variant cutout. Loads `imageUrl` (a CORS-enabled
 * image CDN URL), lets the admin drag a crop box, and on OK returns the
 * cropped region as a PNG File via `onCropped`.
 */
export const CropDialog = ({ open, imageUrl, onCancel, onCropped }) => {
    const imgRef = useRef(null);
    const [crop, setCrop] = useState(null);
    const [completedCrop, setCompletedCrop] = useState(null);
    const [busy, setBusy] = useState(false);

    const onImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        // Start with a centered 80% box so there's always a selection to drag.
        const initial = centerCrop(
            makeAspectCrop({ unit: '%', width: 80 }, width / height, width, height),
            width,
            height,
        );
        setCrop(initial);
    };

    const toBlob = (canvas) =>
        new Promise((resolve, reject) =>
            canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Canvas is empty'))), 'image/png'),
        );

    const handleOk = async () => {
        const image = imgRef.current;
        if (!image || !completedCrop?.width || !completedCrop?.height) {
            message.warning('Drag a crop area first');
            return;
        }
        setBusy(true);
        try {
            const scaleX = image.naturalWidth / image.width;
            const scaleY = image.naturalHeight / image.height;
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(completedCrop.width * scaleX));
            canvas.height = Math.max(1, Math.round(completedCrop.height * scaleY));
            const ctx = canvas.getContext('2d');
            ctx.drawImage(
                image,
                completedCrop.x * scaleX,
                completedCrop.y * scaleY,
                completedCrop.width * scaleX,
                completedCrop.height * scaleY,
                0,
                0,
                canvas.width,
                canvas.height,
            );
            const blob = await toBlob(canvas);
            const file = new File([blob], 'cutout.png', { type: 'image/png' });
            await onCropped(file);
        } catch (e) {
            // A tainted canvas (CORS) is the likely failure here.
            message.error(e.message || 'Failed to crop image');
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open={open}
            title="Crop cutout"
            onCancel={onCancel}
            onOk={handleOk}
            okText="Apply crop"
            confirmLoading={busy}
            destroyOnHidden
            width={640}
        >
            {imageUrl && (
                <div style={{ textAlign: 'center', background: '#fafafa', padding: 8 }}>
                    <ReactCrop
                        crop={crop}
                        onChange={(_, percentCrop) => setCrop(percentCrop)}
                        onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
                    >
                        <img
                            ref={imgRef}
                            src={imageUrl}
                            alt="crop source"
                            crossOrigin="anonymous"
                            onLoad={onImageLoad}
                            style={{ maxWidth: '100%', maxHeight: '60vh' }}
                        />
                    </ReactCrop>
                </div>
            )}
        </Modal>
    );
};
